// ── StandaloneCGCanvas.js ─────────────────────────────────────────────────────
// Self-contained cold gas thruster simulation.
//
// Thruster:  F = 10 mN,  J_mib = 0.02 Ns  →  burn time per pulse = 2 s
// Propellant: Isp = 65 s (N₂ cold gas)  →  mass/pulse = J/(Isp·g₀) = 31.35 mg
// Strategy:  bang-bang — N accel pulses → coast → N retro pulses → stop at 100 mm
//
// Physics verified:
//   dv/pulse = J/m,  a = F/m during burn,  x(t) = ½·a·t² (accel), linear (coast)
//   For m=8kg: N=4, v_max=10mm/s, accel dist=40mm, coast=20mm, retro=40mm = 100mm ✓

import { useState, useRef, useEffect, useCallback } from "react";
import { useTheme, mono } from "./appHelpers";

// ── Physical constants ─────────────────────────────────────────────────────────
const F_THRUST  = 0.01;   // N   — 10 mN thrust
const J_MIB     = 0.02;   // Ns  — minimum impulse bit
const ISP       = 65;     // s   — cold gas N₂ specific impulse
const G0        = 9.81;   // m/s²
const TAU       = J_MIB / F_THRUST;    // 2 s burn per pulse (fixed by hardware)
const SIM_SPD   = 2;      // × real time

const MFONT = "'JetBrains Mono', monospace";

// ── Canvas geometry ────────────────────────────────────────────────────────────
const CW = 820;
const CH = 328;
const TX0 = 72;          // track left  (satellite start)
const TY  = 110;          // satellite centre Y
const SW  = 54, SH = 28;  // satellite block width, height

// ── Pure trajectory planner ────────────────────────────────────────────────────
// targetTime: desired physics maneuver time (s). Null → use minimum time (N_max pulses).
//
// Key insight: MORE time allowed → FEWER pulses needed → lower v_max → LESS propellant
//   N=1 → longest time, least propellant
//   N_max → shortest time, most propellant
//
// Solve: total_t(N) = 2·N·TAU + (TARGET − 2·x_accel(N)) / (N·dv)
//   We search N=1..N_max and pick the smallest N where total_t(N) ≤ targetTime.
function planTrajectory(m, targetM = 0.1, targetTime = null) {
  const dv      = J_MIB / m;          // Δv per pulse (m/s)
  const a       = F_THRUST / m;       // acceleration during burn (m/s²)
  const prop_pp = J_MIB / (ISP * G0); // kg propellant per pulse

  // N_max: largest N where accel+retro distance doesn't exceed targetM (so coast≥0)
  const N_max = Math.max(1, Math.floor(Math.sqrt(targetM / (dv * TAU))));

  // For a given n, compute total physics time
  function totalTime(n) {
    const x_a = 0.5 * a * Math.pow(n * TAU, 2);
    const cd  = targetM - 2 * x_a;
    if (cd < 0) return Infinity;          // accel+retro exceeds target — invalid
    return 2 * n * TAU + cd / (n * dv);
  }

  let N = N_max; // default = fastest (minimum time, most pulses)
  if (targetTime !== null && targetTime > 0) {
    // Walk up from N=1; first N whose total time fits ≤ targetTime wins (fewest pulses)
    for (let n = 1; n <= N_max; n++) {
      if (totalTime(n) <= targetTime) { N = n; break; }
    }
    // If even N=1 is too slow for targetTime, N_max was already set as fallback
  }

  const v_max        = N * dv;
  const x_accel_end  = 0.5 * a * Math.pow(N * TAU, 2);
  const x_retro_st   = targetM - x_accel_end;
  const coast_dist   = x_retro_st - x_accel_end;
  const coast_t      = coast_dist / v_max;

  const t_accel_end  = N * TAU;
  const t_coast_end  = t_accel_end + coast_t;
  const t_retro_end  = t_coast_end + N * TAU;

  const t_N1 = totalTime(1);  // slowest possible (N=1) — for feasibility display
  const t_Nm = totalTime(N_max); // fastest possible — minimum time

  return {
    m, a, dv, prop_pp, N, N_max, v_max,
    x_accel_end, x_retro_st,
    t_accel_end, t_coast_end, t_retro_end,
    total_t:    t_retro_end,
    total_prop: 2 * N * prop_pp,
    coast_mm:   coast_dist * 1000,
    accel_mm:   x_accel_end * 1000,
    coast_t,
    t_min: t_Nm,   // minimum achievable time (N_max pulses)
    t_max: t_N1,   // maximum achievable time (N=1 pulse per phase)
  };
}

// ── Physics state at sim-time t ────────────────────────────────────────────────
function physicsAt(simT, plt, targetM = 0.1) {
  const { a, v_max, dv, prop_pp, N,
          x_accel_end, x_retro_st,
          t_accel_end, t_coast_end, t_retro_end } = plt;

  if (simT <= t_accel_end) {
    // Continuous thrust — velocity rises linearly, position is parabolic
    const vel    = a * simT;
    const pulses = Math.min(N, Math.floor(simT / TAU) + 1);
    return {
      phase: 'accel', pos: 0.5 * a * simT * simT,
      vel, dv_acc: vel, pulses, rpulses: 0,
      prop: pulses * prop_pp, simT,
    };
  }

  if (simT <= t_coast_end) {
    const tc = simT - t_accel_end;
    return {
      phase: 'coast', pos: x_accel_end + v_max * tc,
      vel: v_max, dv_acc: v_max, pulses: N, rpulses: 0,
      prop: N * prop_pp, simT,
    };
  }

  if (simT < t_retro_end) {
    const tr     = simT - t_coast_end;
    const vel    = Math.max(0, v_max - a * tr);
    const rp     = Math.min(N, Math.floor(tr / TAU) + 1);
    return {
      phase: 'retro',
      pos:  x_retro_st + v_max * tr - 0.5 * a * tr * tr,
      vel, dv_acc: vel, pulses: N, rpulses: rp,
      prop: (N + rp) * prop_pp, simT,
    };
  }

  return {
    phase: 'done', pos: targetM, vel: 0, dv_acc: 0,
    pulses: N, rpulses: N, prop: 2 * N * prop_pp, simT,
  };
}

// ── Helpers ────────────────────────────────────────────────────────────────────
function rr(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x+r,y); ctx.lineTo(x+w-r,y); ctx.arcTo(x+w,y,x+w,y+r,r);
  ctx.lineTo(x+w,y+h-r); ctx.arcTo(x+w,y+h,x+w-r,y+h,r);
  ctx.lineTo(x+r,y+h); ctx.arcTo(x,y+h,x,y+h-r,r);
  ctx.lineTo(x,y+r); ctx.arcTo(x,y,x+r,y,r);
  ctx.closePath();
}

function kg2mg(kg) { return kg * 1e6; }   // 1 kg = 1 000 000 mg

// ── Main draw function (module-level, pure — no closures over component state) ─
function drawAll(ctx, plt, s, Cx, targetM = 0.1) {
  const targetMM = Math.round(targetM * 1000);
  const TX1 = TX0 + (CW - TX0 - 72);
  const TPX = TX1 - TX0;
  const VS = TPX / targetM;

  // ── Background
  ctx.fillStyle = Cx.bg; ctx.fillRect(0, 0, CW, CH);

  // ── Phase zone bars (above satellite track) ────────────────────────────────
  if (plt) {
    const zY = TY - SH / 2 - 30;
    const zH = 16;
    const xA = TX0 + plt.x_accel_end * VS;
    const xR = TX0 + plt.x_retro_st  * VS;

    // Filled zones
    ctx.fillStyle = '#0e9cc018'; ctx.fillRect(TX0, zY, xA - TX0, zH);
    ctx.fillStyle = '#22c55518'; ctx.fillRect(xA, zY, xR - xA, zH);
    ctx.fillStyle = '#ef444418'; ctx.fillRect(xR, zY, TX1 - xR, zH);

    // Zone borders
    ctx.strokeStyle = '#0e9cc040'; ctx.lineWidth = 0.5;
    ctx.strokeRect(TX0, zY, xA - TX0, zH);
    ctx.strokeStyle = '#22c55540';
    ctx.strokeRect(xA, zY, xR - xA, zH);
    ctx.strokeStyle = '#ef444440';
    ctx.strokeRect(xR, zY, TX1 - xR, zH);

    // Zone labels
    ctx.textAlign = 'center'; ctx.font = `bold 8px ${MFONT}`;
    ctx.fillStyle = '#0e9cc0cc';
    ctx.fillText(`ACCEL  ${plt.N} × MIB`, (TX0 + xA) / 2, zY + 11);
    ctx.fillStyle = '#22c55ecc';
    ctx.fillText(`COAST  ${plt.coast_mm.toFixed(1)} mm`, (xA + xR) / 2, zY + 11);
    ctx.fillStyle = '#ef4444cc';
    ctx.fillText(`RETRO  ${plt.N} × MIB`, (xR + TX1) / 2, zY + 11);

    // Active zone highlight
    if (s) {
      const activeX = s.phase === 'accel' ? TX0
                    : s.phase === 'coast' ? xA
                    : s.phase === 'retro' ? xR : null;
      const activeW = s.phase === 'accel' ? xA - TX0
                    : s.phase === 'coast' ? xR - xA
                    : s.phase === 'retro' ? TX1 - xR : 0;
      if (activeX !== null) {
        ctx.fillStyle = s.phase === 'accel' ? '#0e9cc030'
                      : s.phase === 'coast' ? '#22c55530'
                      : '#ef444430';
        ctx.fillRect(activeX, zY, activeW, zH);
      }
    }
  }

  // ── Ground rail ────────────────────────────────────────────────────────────
  const railY = TY + SH / 2 + 6;
  ctx.strokeStyle = Cx.border + '80'; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(TX0 - 10, railY); ctx.lineTo(TX1 + 14, railY); ctx.stroke();

  // Progress glow trail
  if (s && s.pos > 0) {
    const trX = Math.min(TX1, TX0 + s.pos * VS);
    const col = s.phase === 'retro' ? '#ef4444' : '#0e9cc0';
    const g = ctx.createLinearGradient(TX0, 0, trX, 0);
    g.addColorStop(0, col + '00'); g.addColorStop(1, col + '40');
    ctx.fillStyle = g;
    ctx.fillRect(TX0, railY - 2, trX - TX0, 3);
  }

  // ── Ruler: tick marks + labels ─────────────────────────────────────────────
  const rulerStep = targetMM <= 50 ? 5 : targetMM <= 200 ? 10 : targetMM <= 500 ? 25 : 50;
  const majorStep = rulerStep * 5;
  for (let mm = 0; mm <= targetMM; mm += rulerStep) {
    const mx = TX0 + mm * 0.001 * VS;
    const maj = mm % majorStep === 0 || mm === targetMM;
    const tH  = maj ? 13 : 7;
    ctx.strokeStyle = mm === targetMM ? '#f59e0bcc'
                    : maj ? Cx.border + 'aa' : Cx.border + '55';
    ctx.lineWidth = mm === targetMM ? 2.5 : maj ? 1 : 0.6;
    ctx.beginPath(); ctx.moveTo(mx, railY); ctx.lineTo(mx, railY + tH); ctx.stroke();
    if (maj || mm === 0) {
      ctx.font = `${maj ? 8 : 7}px ${MFONT}`;
      ctx.fillStyle = mm === targetMM ? '#f59e0b' : Cx.muted;
      ctx.textAlign = 'center';
      ctx.fillText(`${mm}`, mx, railY + tH + 10);
    }
  }
  ctx.font = `7px ${MFONT}`; ctx.fillStyle = Cx.muted; ctx.textAlign = 'center';
  ctx.fillText('mm', TX0 + TPX / 2, railY + 13 + 10 + 10);

  // ── 100 mm target line ─────────────────────────────────────────────────────
  ctx.strokeStyle = '#f59e0bcc'; ctx.lineWidth = 2.5; ctx.setLineDash([4, 3]);
  ctx.beginPath();
  ctx.moveTo(TX1, TY - SH / 2 - 46);
  ctx.lineTo(TX1, railY + 18);
  ctx.stroke(); ctx.setLineDash([]);
  ctx.font = `bold 10px ${MFONT}`; ctx.fillStyle = '#f59e0b'; ctx.textAlign = 'center';
  ctx.fillText(`${targetMM} mm`, TX1, TY - SH / 2 - 50);

  // ── Satellite ──────────────────────────────────────────────────────────────
  const satX = s ? Math.min(TX1 - SW / 2, TX0 + s.pos * VS) : TX0;

  // Solar panels
  ctx.fillStyle = '#1e3a5f'; ctx.strokeStyle = '#0e9cc033'; ctx.lineWidth = 0.8;
  ctx.beginPath(); ctx.rect(satX - SW / 2 - 24, TY - 6, 22, 12); ctx.fill(); ctx.stroke();
  ctx.beginPath(); ctx.rect(satX + SW / 2 + 2,  TY - 6, 22, 12); ctx.fill(); ctx.stroke();

  // Hull
  const hullCol = !s              ? Cx.border
                : s.phase === 'accel' ? '#0e9cc0'
                : s.phase === 'coast' ? '#22c55e'
                : s.phase === 'retro' ? '#ef4444'
                : '#22c55e';
  rr(ctx, satX - SW / 2, TY - SH / 2, SW, SH, 6);
  ctx.fillStyle = hullCol + '1c'; ctx.fill();
  ctx.strokeStyle = hullCol; ctx.lineWidth = 2; ctx.stroke();

  // Centre indicator dot
  ctx.beginPath(); ctx.arc(satX, TY, 4.5, 0, Math.PI * 2);
  ctx.fillStyle = hullCol; ctx.fill();

  // Done banner
  if (s && s.phase === 'done') {
    ctx.font = `bold 11px ${MFONT}`; ctx.fillStyle = '#22c55e'; ctx.textAlign = 'center';
    ctx.fillText(`✓  STOPPED AT ${targetMM} mm`, TX1, TY - SH / 2 - 14);
  }

  // ── Thruster plume ─────────────────────────────────────────────────────────
  if (s && (s.phase === 'accel' || s.phase === 'retro')) {
    const isRet  = s.phase === 'retro';
    const nzX    = isRet ? satX + SW / 2 : satX - SW / 2;
    const dir    = isRet ? 1 : -1;
    const pCol   = isRet ? '#ef4444' : '#22c55e';
    const rgb    = isRet ? '239,68,68' : '34,197,94';
    const pLen   = 30 + Math.sin(s.simT * 44) * 7;

    // Cone
    ctx.fillStyle = pCol + '65';
    ctx.beginPath();
    ctx.moveTo(nzX, TY - 9);
    ctx.lineTo(nzX + dir * pLen, TY);
    ctx.lineTo(nzX, TY + 9);
    ctx.fill();

    // Glow halo
    const g = ctx.createRadialGradient(nzX + dir * 5, TY, 1, nzX + dir * 12, TY, 26);
    g.addColorStop(0, pCol + 'aa'); g.addColorStop(1, pCol + '00');
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.arc(nzX + dir * 12, TY, 26, 0, Math.PI * 2); ctx.fill();

    // Exhaust particles
    for (let i = 0; i < 14; i++) {
      const phi  = ((s.simT * 19 + i / 14) % 1);
      const dist = 9 + phi * 52;
      const spr  = Math.sin(s.simT * 23 + i * 1.75) * 5.5;
      const al   = (1 - phi) * 0.88;
      const r    = Math.max(0.4, 2.8 * (1 - phi * 0.65));
      ctx.beginPath();
      ctx.arc(nzX + dir * dist, TY + spr, r, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${rgb},${al.toFixed(2)})`; ctx.fill();
    }

    // Pulse label
    const pNum = isRet ? s.rpulses : s.pulses;
    ctx.font = `8px ${MFONT}`;
    ctx.fillStyle = pCol;
    ctx.textAlign = isRet ? 'left' : 'right';
    ctx.fillText(
      `${isRet ? 'RETRO' : 'ACCEL'} PULSE #${pNum}  ·  J = ${(J_MIB * 1000).toFixed(0)} mNs`,
      nzX + dir * (pLen + 6), TY - SH / 2 - 8);
  }

  // ── Data readout panel ─────────────────────────────────────────────────────
  const pY = railY + 13 + 22;   // just below ruler labels
  const pH = CH - pY - 4;

  ctx.fillStyle = Cx.panel + 'cc';
  rr(ctx, 4, pY, CW - 8, pH, 5); ctx.fill();
  ctx.strokeStyle = Cx.border + '60'; ctx.lineWidth = 0.5;
  rr(ctx, 4, pY, CW - 8, pH, 5); ctx.stroke();

  if (!s || s.phase === 'idle') {
    ctx.font = `10px ${MFONT}`; ctx.fillStyle = Cx.muted; ctx.textAlign = 'center';
    ctx.fillText('Press  ▶ RUN MANEUVER  to start', CW / 2, pY + pH / 2 + 4);
    return;
  }

  // Column x positions
  const c1 = 18;
  const c2 = CW * 0.27;
  const c3 = CW * 0.52;
  const c4 = CW * 0.76;

  const row1 = pY + 13;
  const row2 = pY + 30;
  const row3 = pY + 44;

  const phCol = s.phase === 'retro'  ? '#ef4444'
              : s.phase === 'coast'  ? '#22c55e'
              : '#0e9cc0';

  function lbl(x, y, txt, col, sz) {
    ctx.textAlign = 'left'; ctx.font = `${sz}px ${MFONT}`;
    ctx.fillStyle = col; ctx.fillText(txt, x, y);
  }

  // ── Column 1: Distance ─────────────────────────────────────────────────────
  lbl(c1, row1, 'DISTANCE TRAVELED', Cx.muted, 8);
  lbl(c1, row2, `${(s.pos * 1000).toFixed(2)} mm`, '#0e9cc0', 18);
  lbl(c1, row3, `target: ${targetMM}.00 mm`, Cx.muted, 8);

  // ── Column 2: Velocity ─────────────────────────────────────────────────────
  lbl(c2, row1, 'VELOCITY', Cx.muted, 8);
  lbl(c2, row2, `${(s.vel * 1000).toFixed(3)} mm/s`, phCol, 18);
  const vPeak = plt ? `peak ${(plt.v_max * 1000).toFixed(3)} mm/s` : '';
  lbl(c2, row3, vPeak, Cx.muted, 8);

  // Velocity bar
  if (plt) {
    const barW = 140;
    const barH = 5;
    const barX = c2;
    const barY = row2 + 5;
    const fill = Math.min(1, s.vel / plt.v_max);
    ctx.fillStyle = Cx.border + '60';
    rr(ctx, barX, barY, barW, barH, 2); ctx.fill();
    if (fill > 0) {
      ctx.fillStyle = phCol + 'cc';
      rr(ctx, barX, barY, barW * fill, barH, 2); ctx.fill();
    }
  }

  // ── Column 3: Delta-V ──────────────────────────────────────────────────────
  lbl(c3, row1, 'DELTA-V CONSUMED', Cx.muted, 8);
  const dvDir = s.phase === 'retro' ? ' ↓' : ' ↑';
  lbl(c3, row2, `${(s.dv_acc * 1000).toFixed(3)} mm/s${dvDir}`, '#f59e0b', 18);
  const pulseTxt = `a:${s.pulses}  r:${s.rpulses}  (${plt ? plt.N : '?'} each)`;
  lbl(c3, row3, pulseTxt, Cx.muted, 8);

  // ── Column 4: Propellant ───────────────────────────────────────────────────
  const propMg      = kg2mg(s.prop);
  const budgetMg    = plt ? kg2mg(plt.total_prop) : 0;
  const propPulseMg = kg2mg(J_MIB / (ISP * G0));

  lbl(c4, row1, 'PROPELLANT SPENT', Cx.muted, 8);
  lbl(c4, row2, `${propMg.toFixed(2)} mg`, '#a855f7', 18);
  lbl(c4, row3, `budget ${budgetMg.toFixed(2)} mg  (${propPulseMg.toFixed(2)} mg/pulse)`, Cx.muted, 8);

  // Prop bar
  const bW2 = 140, bH2 = 5, bX2 = c4, bY2 = row2 + 5;
  const pf  = budgetMg > 0 ? Math.min(1, propMg / budgetMg) : 0;
  ctx.fillStyle = Cx.border + '60'; rr(ctx, bX2, bY2, bW2, bH2, 2); ctx.fill();
  if (pf > 0) {
    ctx.fillStyle = '#a855f7cc'; rr(ctx, bX2, bY2, bW2 * pf, bH2, 2); ctx.fill();
  }

  // ── Time row (full-width, below the four columns) ─────────────────────────
  const rowT    = row3 + 22;
  const elapsed = s.simT;
  const planned = plt ? plt.total_t : 0;
  const tFrac   = planned > 0 ? Math.min(1, elapsed / planned) : 0;
  const tCol    = s.phase === 'done' ? '#22c55e' : '#f59e0b';
  const barTW   = CW - c1 - 22;

  // Divider
  ctx.strokeStyle = Cx.border + '40'; ctx.lineWidth = 0.5;
  ctx.beginPath(); ctx.moveTo(c1, rowT - 8); ctx.lineTo(CW - 14, rowT - 8); ctx.stroke();

  // Label + values
  lbl(c1, rowT + 2, 'TIME', Cx.muted, 7);

  ctx.textAlign = 'left';
  ctx.font = `bold 11px ${MFONT}`; ctx.fillStyle = tCol;
  ctx.fillText(`${elapsed.toFixed(2)} s`, c1 + 38, rowT + 3);

  ctx.font = `9px ${MFONT}`; ctx.fillStyle = Cx.muted;
  ctx.fillText(` /  ${planned.toFixed(2)} s planned`, c1 + 38 + 72, rowT + 3);

  // Right side: done result or real-wall estimate
  ctx.textAlign = 'right';
  if (s.phase === 'done') {
    ctx.font = `bold 10px ${MFONT}`; ctx.fillStyle = '#22c55e';
    ctx.fillText(`✓  MANEUVER COMPLETE  —  ${elapsed.toFixed(2)} s sim`, CW - 16, rowT + 3);
  } else {
    ctx.font = `9px ${MFONT}`; ctx.fillStyle = Cx.muted;
    ctx.fillText(`${(tFrac * 100).toFixed(0)}% of maneuver done`, CW - 16, rowT + 3);
  }

  // Progress bar (full width)
  const barTY = rowT + 9;
  ctx.fillStyle = Cx.border + '38';
  rr(ctx, c1, barTY, barTW, 6, 3); ctx.fill();
  if (tFrac > 0) {
    ctx.fillStyle = tCol + 'cc';
    rr(ctx, c1, barTY, barTW * tFrac, 6, 3); ctx.fill();
  }
  // Phase labels on bar
  if (plt) {
    const accelFrac  = plt.t_accel_end / planned;
    const coastFrac  = plt.t_coast_end / planned;
    const markStyle  = (frac, label, col) => {
      if (frac > 0.02 && frac < 0.98) {
        const mx = c1 + barTW * frac;
        ctx.strokeStyle = col + '66'; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(mx, barTY - 1); ctx.lineTo(mx, barTY + 7); ctx.stroke();
        ctx.font = `6px ${MFONT}`; ctx.fillStyle = col + 'aa'; ctx.textAlign = 'center';
        ctx.fillText(label, mx, barTY + 15);
      }
    };
    markStyle(accelFrac,  'COAST▶', '#22c55e');
    markStyle(coastFrac,  'RETRO▶', '#ef4444');
  }
}

// ── Component ──────────────────────────────────────────────────────────────────
export default function StandaloneCGCanvas({ mass: massProp = 8, targetMM: targetMMProp = 100 }) {
  const [massInput, setMassInput]         = useState(String(massProp));
  const [targetInput, setTargetInput]     = useState(String(targetMMProp));
  const [targetTimeInput, setTargetTime]  = useState('');   // desired physics maneuver time (s)
  const [status, setStatus]               = useState('idle'); // idle | running | done

  const canvasRef  = useRef(null);
  const rafRef     = useRef(null);
  const startRef   = useRef(null);
  const planRef    = useRef(null);
  const snapRef    = useRef(null);
  const themeRef   = useRef(null);
  const simSpdRef  = useRef(SIM_SPD);   // dynamic sim speed (updated on Run)

  const theme      = useTheme();
  themeRef.current = theme;

  // ── Render one frame ───────────────────────────────────────────────────────
  const render = useCallback((s) => {
    const canvas = canvasRef.current;
    const Cx     = themeRef.current;
    if (!canvas || !Cx) return;
    drawAll(canvas.getContext('2d'), planRef.current, s, Cx, planRef.current?.targetM ?? 0.1);
  }, []);

  // ── RAF tick ───────────────────────────────────────────────────────────────
  const tick = useCallback((ts) => {
    if (!startRef.current) startRef.current = ts;
    const simT = ((ts - startRef.current) / 1000) * simSpdRef.current;
    const plt  = planRef.current;
    if (!plt) return;

    const s = physicsAt(simT, plt, plt.targetM ?? 0.1);
    snapRef.current = s;
    render(s);

    if (s.phase !== 'done') {
      rafRef.current = requestAnimationFrame(tick);
    } else {
      setStatus('done');
    }
  }, [render]);

  // ── Sync target time default when mass changes (show minimum plan time) ───
  useEffect(() => {
    const m   = Math.max(0.5, parseFloat(massInput) || 8);
    const plt = planTrajectory(m, (parseFloat(targetInput) || 100) / 1000, null);   // minimum time plan = N_max
    setTargetTime(plt.total_t.toFixed(1));
  }, [massInput, targetInput]);

  // ── Controls ───────────────────────────────────────────────────────────────
  const handleRun = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const m          = Math.max(0.5, parseFloat(massInput) || 8);
    const targetM_v  = Math.max(0.01, (parseFloat(targetInput) || 100) / 1000);
    const targetTime = parseFloat(targetTimeInput);
    const plt        = planTrajectory(m, targetM_v, isFinite(targetTime) && targetTime > 0 ? targetTime : null);
    plt.targetM      = targetM_v;
    // Always animate in ~8 s of wall time for comfortable viewing
    simSpdRef.current = plt.total_t / 8;
    planRef.current   = plt;
    startRef.current  = null;
    snapRef.current   = null;
    setStatus('running');
    rafRef.current = requestAnimationFrame(tick);
  }, [massInput, targetInput, targetTimeInput, tick]);

  const handleReset = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    planRef.current  = null;
    snapRef.current  = null;
    startRef.current = null;
    setStatus('idle');
    render(null);
  }, [render]);

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  useEffect(() => {
    render(null);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (status !== 'running') render(snapRef.current);
  }, [theme, render, status]);

  // ── JSX ────────────────────────────────────────────────────────────────────
  const Cx = theme ?? {};
  const C  = { muted: Cx.muted ?? '#4b5563', border: Cx.border ?? '#1e2d3d', text: Cx.text ?? '#e2e8f0' };

  // Live plan preview — recomputes whenever mass or target time changes
  const m_prev       = Math.max(0.5, parseFloat(massInput) || 8);
  const tgt_prev     = parseFloat(targetTimeInput);
  const targetM_p    = Math.max(0.01, (parseFloat(targetInput) || 100) / 1000);
  const plt_p        = planTrajectory(m_prev, targetM_p, isFinite(tgt_prev) && tgt_prev > 0 ? tgt_prev : null);
  const tgtFeasible  = !isFinite(tgt_prev) || tgt_prev <= 0 || tgt_prev >= plt_p.t_min;
  const tgtTooSlow   = isFinite(tgt_prev) && tgt_prev > 0 && tgt_prev > plt_p.t_max;

  const btnBase = {
    padding: '5px 16px', ...mono, fontSize: 9, letterSpacing: 1.5,
    cursor: 'pointer', background: 'transparent',
    border: `1px solid ${C.border}`, color: C.muted,
    borderRadius: 4, textTransform: 'uppercase',
  };
  const btnRun = { ...btnBase, borderColor: '#0e9cc0', color: '#0e9cc0' };

  return (
    <div>
      {/* ── Controls row ── */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Mass input */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ ...mono, fontSize: 9, color: C.muted, letterSpacing: 1 }}>SATELLITE MASS</span>
          <input
            type="number" min="0.5" max="50000" step="0.5"
            value={massInput}
            onChange={e => setMassInput(e.target.value)}
            style={{
              width: 76, padding: '3px 7px', ...mono, fontSize: 11,
              background: 'transparent', border: `1px solid ${C.border}`,
              color: C.text, borderRadius: 3, textAlign: 'right',
            }}
          />
          <span style={{ ...mono, fontSize: 9, color: C.muted }}>kg</span>
        </div>

        {/* Target displacement input */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ ...mono, fontSize: 9, color: C.muted, letterSpacing: 1 }}>TARGET DISPLACEMENT</span>
          <input
            type="number" min="1" max="10000" step="10"
            value={targetInput}
            onChange={e => setTargetInput(e.target.value)}
            style={{
              width: 76, padding: '3px 7px', ...mono, fontSize: 11,
              background: 'transparent', border: `1px solid ${C.border}`,
              color: C.text, borderRadius: 4, textAlign: 'right',
            }}
          />
          <span style={{ ...mono, fontSize: 9, color: C.muted }}>mm</span>
        </div>

        {/* Maneuver time input — physics time target, planner picks N accordingly */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ ...mono, fontSize: 9, letterSpacing: 1,
            color: tgtFeasible ? C.muted : '#ef4444' }}>MANEUVER TIME</span>
          <input
            type="number" min="1" max="99999" step="1"
            value={targetTimeInput}
            onChange={e => setTargetTime(e.target.value)}
            style={{
              width: 72, padding: '3px 7px', ...mono, fontSize: 11,
              background: 'transparent',
              border: `1px solid ${tgtFeasible ? C.border : '#ef444466'}`,
              color: tgtFeasible ? C.text : '#ef4444',
              borderRadius: 3, textAlign: 'right',
            }}
          />
          <span style={{ ...mono, fontSize: 9, color: C.muted }}>s</span>
        </div>

        <button
          onClick={handleRun}
          style={status === 'running' ? btnBase : btnRun}
          disabled={status === 'running'}
        >
          {status === 'running' ? '◌ RUNNING…' : '▶ RUN MANEUVER'}
        </button>
        <button onClick={handleReset} style={btnBase}>⟳ RESET</button>

        {/* Live plan preview — all specs that change with N */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={{ ...mono, fontSize: 8, color: C.muted, lineHeight: 1.7 }}>
            {`thruster: F=${(F_THRUST*1000).toFixed(0)}mN  J_mib=${(J_MIB*1000).toFixed(0)}mNs  Isp=${ISP}s  burn=${TAU.toFixed(0)}s/pulse`}
            {`  ·  Δv/pulse=${(plt_p.dv*1000).toFixed(3)}mm/s  @  ${m_prev}kg`}
          </span>
          <span style={{ ...mono, fontSize: 8, lineHeight: 1.7,
            color: tgtFeasible ? '#0e9cc0' : '#ef4444' }}>
            {tgtFeasible
              ? `plan: N=${plt_p.N} pulses/phase  v_max=${(plt_p.v_max*1000).toFixed(3)}mm/s  accel=${plt_p.accel_mm.toFixed(1)}mm  coast=${plt_p.coast_mm.toFixed(1)}mm  retro=${plt_p.accel_mm.toFixed(1)}mm  →  ${plt_p.total_t.toFixed(2)}s  ·  prop=${kg2mg(plt_p.total_prop).toFixed(2)}mg  (${2*plt_p.N} pulses total)`
              : `⚠ target ${tgt_prev.toFixed(1)}s < minimum ${plt_p.t_min.toFixed(1)}s — using N=${plt_p.N_max} (fastest plan)`}
            {tgtTooSlow ? `  [N=1 gives max ${plt_p.t_max.toFixed(1)}s; coast fills gap]` : ''}
          </span>
          <span style={{ ...mono, fontSize: 7, color: C.muted }}>
            {`feasible range: ${plt_p.t_min.toFixed(1)}s (N=${plt_p.N_max}, max prop) … ${plt_p.t_max.toFixed(1)}s (N=1, min prop=${kg2mg(J_MIB/(ISP*G0)*2).toFixed(2)}mg)`}
          </span>
        </div>
      </div>

      <canvas
        ref={canvasRef}
        width={CW} height={CH}
        style={{ width: '100%', height: 'auto', display: 'block' }}
      />
    </div>
  );
}
